import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { request, requestV3 } from 'Features/Auth/axios-client'
import {
  CreateProjectVariables,
  DeleteProjectVariables,
  useCreateProjectDataParams,
  useDeleteProjectParams,
} from './ProjectList.types'
import { UpdateProjectAccessParams } from '../ProjectCard/ProjectCard.types'

const deleteProject = async ({ projectId }: DeleteProjectVariables) => {
  const { data } = await requestV3({
    url: `/projects?projectId=${projectId}`,
    method: 'delete',
  })
  return data
}

export const useDeleteProject = ({
  onDeleteSuccess,
  onDeleteError,
}: useDeleteProjectParams) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: async (_data, variables: DeleteProjectVariables) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      onDeleteSuccess(variables.projectId)
    },
    onError: () => {
      onDeleteError()
    },
  })
}

const fetchProjectList = async () => {
  const { data } = await requestV3({
    url: `/projects`,
  })
  return data
}

export const useProjectListData = () => {
  return useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjectList,
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    refetchInterval: 60000, // 1 minute
  })
}

const createProject = async ({ projectName }: CreateProjectVariables) => {
  const { data } = await requestV3({
    url: `/projects`,
    method: 'post',
    data: {
      projectName,
    },
  })
  return data
}

export const useCreateProjectData = ({
  onCreateProjectSuccess,
}: useCreateProjectDataParams) => {
  return useMutation({
    mutationFn: createProject,
    onSuccess: ({ data, message }) => {
      onCreateProjectSuccess(data.id, message)
    },
  })
}

const updateProjectAccess = async ({
  projectId,
  projectAccess,
}: UpdateProjectAccessParams) => {
  const { data } = await request({
    url: `/projects/${projectId}/update-access`,
    method: 'put',
    data: projectAccess,
  })
  return data
}

export const useUpdateProjectAccess = (
  onProjectAccessUpdateSuccess: () => void,
  onProjectAccessUpdateFail: () => void,
  apiToInvalidate: string[] = ['projects']
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateProjectAccess,
    onSuccess: async () => {
      // Invalidate queries one by one
      await Promise.all(
        apiToInvalidate.map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] })
        )
      )
      onProjectAccessUpdateSuccess()
    },
    onError: () => {
      onProjectAccessUpdateFail()
    },
  })
}
